// src/riskFlow/RiskSummaryReport.js
// Professional dashboard-style risk assessment summary - Mantine version

import React from 'react';
import { useSelector } from 'react-redux';
import {
  Card, Text, Stack, Group, Container, Badge, Grid,
  ThemeIcon, Timeline, Progress, Title,
  Paper, RingProgress, SimpleGrid, Center, Button
} from '@mantine/core';
import {
  IconTarget, IconShield, IconCheck, IconAlertTriangle,
  IconBulb, IconTrendingUp, IconAward,
  IconSearch, IconReport, IconArrowLeft,
  IconChartBar, IconCheckbox, IconClipboardCheck
} from '@tabler/icons-react';


// Color mappings for visual consistency
const RISK_PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: 'white' },
  High: { bg: '#993333', text: 'white' },
  Significant: { bg: '#B94D4D', text: 'white' },
  Moderate: { bg: '#D4AA7D', text: 'white' },
  Low: { bg: '#B8C2B7', text: 'white' },
  Negligible: { bg: '#D8D8D8', text: 'dark' },
};

// const ACTION_PLAN_COLORS = {
//   Excellent: { bg: '#4CAF50', color: 'green' },
//   'Very Good': { bg: '#66BB6A', color: 'green' },
//   Good: { bg: '#8BC34A', color: 'green' },
//   Fair: { bg: '#FFC107', color: 'yellow' },
//   Poor: { bg: '#FF5722', color: 'orange' },
//   Unsatisfactory: { bg: '#D32F2F', color: 'red' },
// };

const RESIDUAL_RISK_COLORS = {
  Negligible: { bg: '#D8D8D8', color: 'gray' },
  Low: { bg: '#B8C2B7', color: 'green' },
  Moderate: { bg: '#D4AA7D', color: 'yellow' },
  Significant: { bg: '#B94D4D', color: 'orange' },
  High: { bg: '#993333', color: 'red' },
  Critical: { bg: '#1A1A1A', color: 'red' },
};

// Calculate completion percentage based on assessment completeness
function calculateCompletionScore(risk, actions, residualData) {
  let score = 0;
  if (risk?.riskName) score += 15; // Risk identified
  if (risk?.refinedRisk) score += 10; // Risk refined
  if (risk?.priority) score += 25; // Risk evaluated
  if (actions?.length > 0) score += 25; // Actions generated
  if (residualData?.action_plan_rating && residualData?.action_plan_rating !== 'N/A') score += 15; // Action plan assessed
  if (residualData?.residual_rating && residualData?.residual_rating !== 'N/A') score += 10; // Residual risk assessed
  return Math.min(100, score);
}

// Get assessment quality score based on ratings  
function getQualityScore(residualData) {
  const actionPlanScore = {
    'Excellent': 100, 'Very Good': 85, 'Good': 70, 
    'Fair': 55, 'Poor': 30, 'Unsatisfactory': 10
  };
  
  const residualScore = {
    'Negligible': 100, 'Low': 85, 'Moderate': 70,
    'Significant': 55, 'High': 30, 'Critical': 10
  };

  const apScore = actionPlanScore[residualData?.action_plan_rating] || 50;
  const rrScore = residualScore[residualData?.residual_rating] || 50;
  
  return Math.round((apScore + rrScore) / 2);
}

export default function RiskSummaryReport({ 
  modelId, 
  assessmentUuid, 
  riskArea, 
  source, 
  onBack,
  // New props for consolidated view
  allRisks = [],
  allSources = [],
  isConsolidatedView = false,
  selectedRisk = null // For direct risk data in reporting phase
}) {
  // Get risks data - either single risk or all risks for consolidated view
  const sectionRisks = useSelector(s => 
    s.risks.models[modelId]?.riskFormData?.[riskArea]?.sectionRisks || {}
  );
  
  const savedRisk = isConsolidatedView 
    ? null // No single risk in consolidated view
    : selectedRisk || (sectionRisks[source?.name] || []).find(risk => risk.status === 'Saved');
    
  // For consolidated view, get all saved risks
  const allSavedRisks = isConsolidatedView 
    ? Object.values(sectionRisks).flat().filter(risk => risk.status === 'Saved')
    : [];
  
  // Get causes data
  const sourceCauses = useSelector(s => {
    if (isConsolidatedView) return {};
    
    // Try multiple approaches to find causes data
    const riskFormData = s.risks.models[modelId]?.riskFormData?.[riskArea];
    
    // First try: from riskSources array
    const fromRiskSources = riskFormData?.riskSources?.find(src => src.name === source?.name)?.causes;
    if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
      console.log('[RiskSummary] Found causes from riskSources:', Object.keys(fromRiskSources).length, 'causes');
      return fromRiskSources;
    }
    
    // Second try: from causesData if it exists
    const fromCausesData = riskFormData?.causesData?.[source?.name];
    if (fromCausesData && Object.keys(fromCausesData).length > 0) {
      console.log('[RiskSummary] Found causes from causesData:', Object.keys(fromCausesData).length, 'causes');
      return fromCausesData;
    }
    
    // Third try: check if causes are stored under a different structure
    console.log('[RiskSummary] No causes found. Detailed debugging:', {
      sourceName: source?.name,
      hasRiskFormData: !!riskFormData,
      riskFormDataKeys: Object.keys(riskFormData || {}),
      hasRiskSources: !!riskFormData?.riskSources,
      riskSourcesCount: riskFormData?.riskSources?.length || 0,
      riskSources: riskFormData?.riskSources?.map(src => ({
        name: src.name,
        hasCauses: !!src.causes,
        causesKeys: src.causes ? Object.keys(src.causes) : []
      })),
      hasCausesData: !!riskFormData?.causesData,
      causesDataKeys: riskFormData?.causesData ? Object.keys(riskFormData.causesData) : []
    });
    
    return {};
  });
  
  // Get actions
  const allActionPlan = useSelector(s => s.controls.actionPlan || []);
  const allControls = useSelector(s => s.controls.controls || {});
  
  let riskActions = [];
  if (savedRisk) {
    // Try multiple approaches to find action data
    const fromActionPlan = allActionPlan.filter(action => 
      action.modelId === modelId && 
      action.riskIds?.includes(savedRisk.riskId) &&
      action.status === 'generated'
    );
    
    const fromControls = allControls[savedRisk.riskId] || [];
    
    console.log('[RiskSummary] Actions data:', {
      riskId: savedRisk.riskId,
      fromActionPlan: fromActionPlan.length,
      fromControls: fromControls.length,
      allActionPlanCount: allActionPlan.length,
      allControlsKeys: Object.keys(allControls)
    });
    
    // Use the approach that has data
    riskActions = fromActionPlan.length > 0 ? fromActionPlan : fromControls;
  }
    
  const savedControls = useSelector(s => s.controls.savedControls || {});
  const savedActionIds = new Set(
    Object.values(savedControls).flat()
      .filter(c => c.modelId === modelId)
      .map(c => c.controlId)
  );
  
  // Get residual risk data
  const residualData = useSelector(s => 
    s.residualRisk.byModel[modelId]?.[savedRisk?.riskId] || {}
  );

  if (!savedRisk && !isConsolidatedView) {
    return (
      <Container fluid px="xl" py="xl">
        <Center>
          <Text color="dimmed">No risk data available for summary</Text>
        </Center>
      </Container>
    );
  }

  // Early return for consolidated view
  if (isConsolidatedView) {
    return (
      <Container fluid px="xl" py="xl">
        <Stack spacing="xl">
          {/* Consolidated Header */}
          <Card withBorder radius="md" p="xl" style={{ 
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            color: 'white'
          }}>
            <Stack spacing="md" align="flex-start">
              <ThemeIcon size={60} radius="50%" color="white" variant="light">
                <IconAward size={32} color="#28a745" />
              </ThemeIcon>
              
              <div>
                <Text size="xl" weight={700} mb="xs">
                  Assessment Complete - Monitoring & Reporting 📊
                </Text>
                <Text size="md" opacity={0.9}>
                  Risk Area: {riskArea}
                </Text>
                <Text size="sm" opacity={0.7} mt="xs">
                  All sources processed • All risks evaluated • Ready for monitoring
                </Text>
              </div>

              <Group spacing="xl">
                <div>
                  <Text size="xs" opacity={0.8} tt="uppercase">Sources</Text>
                  <Text size="lg" weight={700}>{allSources.length}</Text>
                </div>
                <div>
                  <Text size="xs" opacity={0.8} tt="uppercase">Risks</Text>
                  <Text size="lg" weight={700}>{allSavedRisks.length}</Text>
                </div>
                <div>
                  <Text size="xs" opacity={0.8} tt="uppercase">Completion</Text>
                  <Text size="lg" weight={700}>100%</Text>
                </div>
              </Group>
            </Stack>
          </Card>

          {/* Risk Summary Cards */}
          {allSavedRisks.length > 0 && (
            <Card withBorder radius="md" p="xl">
              <Stack spacing="lg">
                <Title order={3} c="dark.7">
                  Risk Assessment Summary
                </Title>
                
                <SimpleGrid cols={1} spacing="lg">
                  {allSavedRisks.map((risk, index) => (
                    <Card key={risk.riskId} withBorder radius="md" p="lg" bg="gray.0">
                      <Group position="apart" mb="md">
                        <div>
                          <Text weight={600} size="lg" c="dark.7">
                            {risk.riskName || risk.refinedRisk}
                          </Text>
                          <Text size="sm" c="dimmed" mt="xs">
                            Source: {risk.sourceName || `Risk ${index + 1}`}
                          </Text>
                        </div>
                        <Badge 
                          size="lg" 
                          style={{
                            backgroundColor: RISK_PRIORITY_COLORS[risk.priority]?.bg || '#gray',
                            color: RISK_PRIORITY_COLORS[risk.priority]?.text || 'dark'
                          }}
                        >
                          {risk.priority || 'Unassigned'}
                        </Badge>
                      </Group>
                      
                      {risk.rationale && (
                        <Text size="sm" c="dimmed" style={{ 
                          fontStyle: 'italic',
                          borderLeft: '3px solid #e9ecef',
                          paddingLeft: '12px',
                          marginLeft: '0px'
                        }}>
                          "{risk.rationale}"
                        </Text>
                      )}
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            </Card>
          )}

        </Stack>
      </Container>
    );
  }

  const completionScore = calculateCompletionScore(savedRisk, riskActions, residualData);
  const qualityScore = getQualityScore(residualData);
  
  // Get final ratings
  const finalActionPlanRating = residualData.overridden?.action_plan_rating 
    ?? residualData.action_plan_rating ?? 'N/A';
  const finalResidualRating = residualData.overridden?.residual_rating 
    ?? residualData.residual_rating ?? 'N/A';

  const priorityConfig = RISK_PRIORITY_COLORS[savedRisk.priority] || { bg: '#gray', text: 'dark' };
  const residualConfig = RESIDUAL_RISK_COLORS[finalResidualRating] || { color: 'gray' };

  // Count only causes that have been evaluated (have evaluation data)
  const significantCauses = Object.entries(sourceCauses).filter(([causeKey, causeData]) => {
    // Check if this cause has any evaluation data
    const evaluation = causeData?.evaluation;
    if (!evaluation) return false;
    
    // Check if any evaluation criteria has a value
    return Object.values(evaluation).some(evalData => {
      const value = evalData?.value;
      return value && value.trim() !== '';
    });
  }).length;

  const savedActionsCount = riskActions.filter(a => savedActionIds.has(a.controlId)).length;

  return (
    <Container fluid px="xl" py="xl">
      <Stack spacing="xl">
        {/* Hero Header */}
        <Card withBorder radius="md" p="xl" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <Stack spacing="md" align="center">
            <ThemeIcon size={60} radius="50%" color="white" variant="light">
              <IconAward size={32} color="#667eea" />
            </ThemeIcon>
            
            <div style={{ textAlign: 'center' }}>
              <Text size="xl" weight={700} mb="xs">
                Risk Assessment Complete! 🎉
              </Text>
              <Text size="md" opacity={0.9}>
                {savedRisk.refinedRisk || savedRisk.riskName}
              </Text>
              <Text size="sm" opacity={0.7} mt="xs">
                Source: {source.name} • Area: {riskArea}
              </Text>
            </div>

            <Group spacing="xl">
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" opacity={0.8} tt="uppercase">Completion</Text>
                <Text size="lg" weight={700}>{completionScore}%</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" opacity={0.8} tt="uppercase">Quality Score</Text>
                <Text size="lg" weight={700}>{qualityScore}/100</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" opacity={0.8} tt="uppercase">Actions</Text>
                <Text size="lg" weight={700}>{savedActionsCount}</Text>
              </div>
            </Group>
          </Stack>
        </Card>

        {/* Quick Stats */}
        <SimpleGrid cols={4} spacing="md">
          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" color="blue">
                <IconTarget size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" color="dimmed" tt="uppercase">Priority</Text>
                <Badge 
                  size="lg"
                  style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.text }}
                >
                  {savedRisk.priority || 'Not Set'}
                </Badge>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" color="orange">
                <IconSearch size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" color="dimmed" tt="uppercase">Root Causes</Text>
                <Text size="lg" weight={600}>{significantCauses}</Text>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" color="green">
                <IconShield size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" color="dimmed" tt="uppercase">Mitigations</Text>
                <Text size="lg" weight={600}>{savedActionsCount}/{riskActions.length}</Text>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" color={residualConfig.color}>
                <IconTrendingUp size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" color="dimmed" tt="uppercase">Residual Risk</Text>
                <Badge 
                  size="lg"
                  color={residualConfig.color}
                  style={finalResidualRating !== 'N/A' ? {} : { backgroundColor: '#gray' }}
                >
                  {finalResidualRating}
                </Badge>
              </div>
            </Group>
          </Paper>
        </SimpleGrid>

        <Grid>
          {/* Assessment Journey */}
          <Grid.Col span={8}>
            <Card withBorder radius="md" p="xl" h="100%">
              <Stack spacing="lg">
                <Group>
                  <ThemeIcon size="lg" color="blue" variant="light">
                    <IconClipboardCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text weight={600} size="lg">Assessment Journey</Text>
                    <Text size="sm" color="dimmed">Your comprehensive risk analysis process</Text>
                  </div>
                </Group>

                <Timeline active={6} bulletSize={24} lineWidth={2}>
                  <Timeline.Item 
                    bullet={<IconSearch size={12} />} 
                    title="Risk Identified"
                    color="blue"
                  >
                    <Text color="dimmed" size="sm">
                      {savedRisk.riskName}
                    </Text>
                  </Timeline.Item>

                  <Timeline.Item 
                    bullet={<IconBulb size={12} />} 
                    title="Risk Refined" 
                    color="blue"
                  >
                    <Text color="dimmed" size="sm">
                      {savedRisk.refinedRisk ? 'AI-enhanced risk description' : 'Original description used'}
                    </Text>
                  </Timeline.Item>

                  <Timeline.Item 
                    bullet={<IconAlertTriangle size={12} />} 
                    title="Risk Evaluated" 
                    color="orange"
                  >
                    <Text color="dimmed" size="sm">
                      Priority: {savedRisk.priority || 'Not evaluated'}
                    </Text>
                  </Timeline.Item>

                  <Timeline.Item 
                    bullet={<IconTarget size={12} />} 
                    title="Root Causes Analyzed" 
                    color="yellow"
                  >
                    <Text color="dimmed" size="sm">
                      {significantCauses} significant causes identified
                    </Text>
                  </Timeline.Item>

                  <Timeline.Item 
                    bullet={<IconShield size={12} />} 
                    title="Mitigations Planned" 
                    color="green"
                  >
                    <Text color="dimmed" size="sm">
                      {savedActionsCount} actions saved from {riskActions.length} generated
                    </Text>
                  </Timeline.Item>

                  <Timeline.Item 
                    bullet={<IconChartBar size={12} />} 
                    title="Residual Risk Assessed" 
                    color="grape"
                  >
                    <Text color="dimmed" size="sm">
                      Final assessment: {finalResidualRating} residual risk
                    </Text>
                  </Timeline.Item>
                </Timeline>
              </Stack>
            </Card>
          </Grid.Col>

          {/* Assessment Quality */}
          <Grid.Col span={4}>
            <Stack spacing="md" h="100%">
              {/* Completion Ring */}
              <Card withBorder radius="md" p="xl">
                <Stack align="flex-start" spacing="md">
                  <RingProgress
                    size={120}
                    thickness={12}
                    sections={[
                      { value: completionScore, color: completionScore > 80 ? 'green' : completionScore > 60 ? 'yellow' : 'red' }
                    ]}
                    label={
                      <Center>
                        <div style={{ textAlign: 'center' }}>
                          <Text size="xs" color="dimmed" tt="uppercase">Complete</Text>
                          <Text size="lg" weight={700}>{completionScore}%</Text>
                        </div>
                      </Center>
                    }
                  />
                  <Text size="sm" color="dimmed" ta="left">Assessment Completeness</Text>
                </Stack>
              </Card>

              {/* Quality Score */}
              <Card withBorder radius="md" p="xl">
                <Stack spacing="md">
                  <Group>
                    <ThemeIcon size="sm" color="blue" variant="light">
                      <IconCheckbox size={12} />
                    </ThemeIcon>
                    <Text size="sm" weight={500}>Quality Metrics</Text>
                  </Group>
                  
                  <div>
                    <Group position="apart" mb="xs">
                      <Text size="xs" color="dimmed">Mitigation Effectiveness</Text>
                      <Text size="xs" weight={500}>{finalActionPlanRating}</Text>
                    </Group>
                    <Progress 
                      value={qualityScore} 
                      color={qualityScore > 70 ? 'green' : qualityScore > 50 ? 'yellow' : 'red'}
                      size="sm"
                    />
                  </div>

                  <Badge 
                    size="lg" 
                    variant="light"
                    color={qualityScore > 80 ? 'green' : qualityScore > 60 ? 'yellow' : 'red'}
                    fullWidth
                  >
                    Overall Score: {qualityScore}/100
                  </Badge>
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Action Items Summary */}
        {riskActions.length > 0 && (
          <Card withBorder radius="md" p="xl">
            <Stack spacing="md">
              <Group>
                <ThemeIcon size="lg" color="green" variant="light">
                  <IconShield size={20} />
                </ThemeIcon>
                <div>
                  <Text weight={600} size="lg">Mitigation Actions</Text>
                  <Text size="sm" color="dimmed">{savedActionsCount} saved • {riskActions.length - savedActionsCount} pending</Text>
                </div>
              </Group>

              <SimpleGrid cols={2} spacing="md">
                {riskActions.slice(0, 4).map(action => (
                  <Paper key={action.controlId} withBorder p="md" radius="sm">
                    <Group>
                      <ThemeIcon 
                        size="sm" 
                        color={savedActionIds.has(action.controlId) ? 'green' : 'gray'}
                        variant="light"
                      >
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text size="sm" style={{ flex: 1 }}>{action.name}</Text>
                    </Group>
                  </Paper>
                ))}
              </SimpleGrid>

              {riskActions.length > 4 && (
                <Text size="sm" color="dimmed" ta="left">
                  And {riskActions.length - 4} more actions...
                </Text>
              )}
            </Stack>
          </Card>
        )}

        {/* Assessment Summary */}
        <Card withBorder radius="md" p="xl" style={{ 
          background: 'linear-gradient(135deg, #495057 0%, #6c757d 100%)',
          color: 'white'
        }}>
          <Grid>
            <Grid.Col span={8}>
              <Stack spacing="sm">
                <Text size="lg" weight={600}>Assessment Summary</Text>
                <Text size="sm" opacity={0.9}>
                  This {savedRisk.priority?.toLowerCase() || 'assessed'} priority risk from {source.name} has been 
                  thoroughly analyzed with {significantCauses} root causes identified and {savedActionsCount} mitigation 
                  actions planned. The residual risk level is assessed as {finalResidualRating.toLowerCase()} with 
                  {finalActionPlanRating.toLowerCase()} action plan effectiveness.
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack align="flex-start" spacing="lg">
                <Group align="center" spacing="md">
                  <ThemeIcon size={60} radius="50%" color="blue" variant="light">
                    <IconReport size={28} color="#495057" />
                  </ThemeIcon>
                  <Stack spacing="xs">
                    <Text size="lg" weight={600} color="dark">
                      Risk Assessment Complete
                    </Text>
                    <Text size="sm" color="dimmed">
                      Review the summary or continue to next steps
                    </Text>
                  </Stack>
                </Group>
                
                {/* Back to individual risk navigation */}
                {onBack && (
                  <Group spacing="md" mt="md">
                    <Button
                      variant="outline"
                      leftIcon={<IconArrowLeft size={16} />}
                      onClick={onBack}
                      color="blue"
                      size="md"
                    >
                      Back to Residual Risk Assessment
                    </Button>
                  </Group>
                )}
                
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
}